"use client";

import { useRef, useState } from "react";
import { FreeMode, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper/types";
import ArrowRightIcon from "@/app/shared-components/icons/ArrowRightIcon";
import { WinnerCategories } from "../types";
import { CATEGORY_ROUTE_PREFIX, buildCategoryHref, categoryToSlug } from "../category-utils";

import "swiper/css";
import "swiper/css/navigation";

interface CategoryTabsProps {
  categories: WinnerCategories[];
}

const ALL_WINNERS_LABEL = "All winners";
const ALL_WINNERS_HREF = "/winners";

const getInitialPathname = () => (typeof window !== "undefined" ? window.location.pathname : ALL_WINNERS_HREF);

const getActiveSlug = (pathname: string): string | null => {
  if (!pathname.startsWith(CATEGORY_ROUTE_PREFIX)) return null;
  const rest = pathname.slice(CATEGORY_ROUTE_PREFIX.length).replace(/\/$/, "");
  return rest || null;
};

const CategoryTabs = ({ categories }: CategoryTabsProps) => {
  const [pathname] = useState(getInitialPathname);
  const activeSlug = getActiveSlug(pathname);
  const activeIndex =
    activeSlug === null ? 0 : Math.max(0, categories.findIndex(c => categoryToSlug(c) === activeSlug) + 1);

  const swiperRef = useRef<SwiperType | null>(null);

  const btnSwiperClasses =
    "hidden lg:flex absolute z-[1] inset-y-0 items-center px-3 h-full cursor-pointer select-none transition-all duration-200 ease-in-out text-[#898994] from-white from-30% to-white/0 lg:min-w-[90px]";

  const tabClass = (isActive: boolean) =>
    `flex items-center justify-center whitespace-nowrap px-2 lg:px-3 pt-3 lg:pt-5 pb-3 lg:pb-5 font-jost text-sm lg:text-base font-medium leading-5 lg:leading-[22px] border-b-2 border-solid transition-colors cursor-pointer ${
      isActive ? "text-primary border-primary" : "text-secondary border-transparent"
    }`;

  return (
    <div className="relative w-full">
      <Swiper
        onSwiper={swiper => {
          swiperRef.current = swiper;
          requestAnimationFrame(() => {
            swiper.update();
            if (activeIndex > 0) swiper.slideTo(activeIndex, 0);
          });
        }}
        navigation={{
          nextEl: ".category-tabs-next-btn",
          prevEl: ".category-tabs-prev-btn",
          disabledClass: "opacity-0 invisible",
        }}
        modules={[FreeMode, Navigation]}
        slidesPerView={"auto"}
        spaceBetween={12}
        freeMode={true}
        breakpoints={{
          0: {
            centerInsufficientSlides: true,
            slidesOffsetBefore: 12,
            slidesOffsetAfter: 12,
            spaceBetween: 12,
          },
          1024: {
            centerInsufficientSlides: true,
            slidesOffsetBefore: 0,
            slidesOffsetAfter: 0,
            spaceBetween: 24,
          },
        }}>
        <SwiperSlide className="!w-auto">
          <a href={ALL_WINNERS_HREF} data-testid="category-tab-all-winners" className={tabClass(activeSlug === null)}>
            {ALL_WINNERS_LABEL}
          </a>
        </SwiperSlide>

        {categories.map(category => {
          const slug = categoryToSlug(category);
          return (
            <SwiperSlide key={category.Key} className="!w-auto">
              <a
                href={buildCategoryHref(category)}
                data-testid={`category-tab-${slug}`}
                className={tabClass(activeSlug === slug)}>
                {category.Value}
              </a>
            </SwiperSlide>
          );
        })}
      </Swiper>

      <button
        type="button"
        aria-label="Previous categories"
        onClick={() => swiperRef.current?.slidePrev()}
        className={`category-tabs-prev-btn bg-gradient-to-r left-0 justify-start ${btnSwiperClasses}`}>
        <ArrowRightIcon className="size-6 shrink-0 rotate-180" />
      </button>

      <button
        type="button"
        aria-label="Next categories"
        onClick={() => swiperRef.current?.slideNext()}
        className={`category-tabs-next-btn bg-gradient-to-l right-0 justify-end ${btnSwiperClasses}`}>
        <ArrowRightIcon className="size-6 shrink-0" />
      </button>
    </div>
  );
};

export default CategoryTabs;
